import * as React from 'react';
import useLabelTranslation from 'src/hooks/useLabelTranslation';
import css from './feedbacks.css';
import type {
  FeedbacksProps,
  INTERVIEW_SCHEDULE,
  CANDIDATE_FEEDBACK,
} from 'src/types/crm';
import {getFeedbackTabs} from 'src/components/crm/constant';
import {useDispatch, useSelector} from 'react-redux';
import {useInflight} from 'src/hooks/redux';
import {size200} from '@spaced-out/ui-design-system/lib/styles/variables/_size';
import {
  getApplicantInformation,
  getCandidateFeedbacks,
  getInterviewSchedules,
  getHmExperienceSurveys,
} from 'src/action-creators/crm';
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  CardActions,
} from '@spaced-out/ui-design-system/lib/components/Card';
import {Button} from '@spaced-out/ui-design-system/lib/components/Button';
import {TabList, Tab} from '@spaced-out/ui-design-system/lib/components/Tabs';
import {Badge} from '@spaced-out/ui-design-system/lib/components/Badge';
import {CircularLoader} from '@spaced-out/ui-design-system/lib/components/CircularLoader';
import {SemanticIcon} from '@spaced-out/ui-design-system/lib/components/Icon';
import {EmptyState} from '@spaced-out/ui-design-system/lib/components/EmptyState';

import FeedbacksPanel from 'src/components/crm/jobs/job-info/overview/feedbacks/feedbacks-panel';
import InterviewFeedback from 'src/components/crm/jobs/job-info/overview/feedbacks/cards/interview-feedback';
import SurveyCard from 'src/components/crm/jobs/job-info/overview/feedbacks/cards/survey-card';
import ApplicationReviewCard from 'src/components/crm/jobs/job-info/overview/feedbacks/cards/application-review-card';
import {CompletedCard} from 'src/components/crm/jobs/job-info/overview/feedbacks/cards/completed-card';
import {
  selectCandidateFeedbacks,
  selectCandidateInterviewSchedules,
  selectCandidateHmExperienceSurveys,
  selectCompletedInterviewSchedules,
  selectCompletedCandidateFeedbacks,
} from 'src/selectors/crm';

import {useHistory} from 'src/rerouter';


type Props = {
  jobId?: string;
  isLoading: boolean;
  from?: string;
};

export const Feedbacks = ({jobId, isLoading, from = 'DASHBOARD'}: Props) => {
  const {t} = useLabelTranslation();
  const history = useHistory();
  const FEEDBACK_TABS = getFeedbackTabs(t);
  const {tabId = 'interviewFeedbacks'} = history.location.query;
  const [selected, setSelected] = React.useState(
    tabId
      ? FEEDBACK_TABS.find((tab) => tab.tabId === tabId) || FEEDBACK_TABS[0]
      : FEEDBACK_TABS[0],
  );
  const dispatch = useDispatch();
  const applicantReviewData = useSelector(selectCandidateFeedbacks);
  const InterviewData = useSelector(selectCandidateInterviewSchedules);
  const surveyData = useSelector(selectCandidateHmExperienceSurveys);
  const completedData = useSelector(selectCompletedInterviewSchedules);
  const completedCandidateFeedbacks = useSelector(
    selectCompletedCandidateFeedbacks,
  );
  const isCandidateFeedbacksLoading = useInflight(
    getCandidateFeedbacks('COMPLETED', jobId),
  );
  const isInterviewSchedulesLoading = useInflight(
    getInterviewSchedules('COMPLETED', jobId),
  );
  const [isPanelOpen, setIsPanelOpen] = React.useState(false);

  const recentInterviewData = React.useMemo(() => {
    return (
      // @ts-expect-error - TS2339 - Property 'content' does not exist on type 'INTERVIEW_SCHEDULE[]'.
      InterviewData?.content?.filter((interview: any) => {
        const interviewDateTime = new Date(interview.endDate);
        const currentDateTime = new Date();
        return interviewDateTime <= currentDateTime;
      }) ?? []
    );
  }, [InterviewData]);
  const updateFeedbacks = () => {
    dispatch(getCandidateFeedbacks('COMPLETED', jobId));
    dispatch(getInterviewSchedules('COMPLETED', jobId));
  };
  const tabsWithCount = FEEDBACK_TABS.map((tab) => {
    const countMap = {
      // @ts-expect-error - TS2339 - Property 'content' does not exist on type 'HM_EXPERIENCE_SURVEY[]'.
      survey: surveyData?.content?.length || 0,
      // @ts-expect-error - TS2339 - Property 'content' does not exist on type 'CANDIDATE_FEEDBACK[]'.
      applicationReview: applicantReviewData?.content?.length || 0,
      interviewFeedbacks: recentInterviewData?.length || 0,
      // @ts-expect-error - TS2339 - Property 'content' does not exist on type 'INTERVIEW_SCHEDULE[]'.
      completed: completedData?.content?.length || 0,
    } as const;
    // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{ readonly survey: any; readonly applicationReview: any; readonly interviewFeedbacks: any; readonly completed: any; }'.
    const count = countMap[tab.tabId] || 0;
    // Don't show count for Completed tab
    const showCount = tab.tabId !== 'completed';
    return {
      ...tab,
      label: `${tab.label ?? ''}${showCount && count ? ` (${count})` : ''}`,
      count: showCount ? count : 0,
    };
  });

  const totalCount: number =
    tabsWithCount.reduce((sum, tab) => sum + tab.count, 0) +
    ((completedData as {content?: INTERVIEW_SCHEDULE[]})?.content?.length ||
      0) +
    ((completedCandidateFeedbacks as {content?: CANDIDATE_FEEDBACK[]})?.content
      ?.length || 0);
  const pendingFeedbackCount =
    tabsWithCount.reduce((sum, tab) => sum + tab.count, 0) || 0;

  const renderContent = () => {
    switch (selected.tabId) {
      case 'applicationReview':
        // @ts-expect-error - TS2339 - Property 'content' does not exist on type 'CANDIDATE_FEEDBACK[]'.
        return applicantReviewData?.content?.length ? (
          // @ts-expect-error - TS2339 - Property 'content' does not exist on type 'CANDIDATE_FEEDBACK[]'.
          applicantReviewData.content
            .slice(0, 2)
            // @ts-expect-error - TS7006 - Parameter 'data' implicitly has an 'any' type. | TS7006 - Parameter 'index' implicitly has an 'any' type.
            .map((data, index) => (
              <ApplicationReviewCard
                key={index}
                applicantReviewData={data}
                jobId={jobId}
                from={from}
              />
            ))
        ) : (
          <EmptyState
            title={t('No_Feedbacks_Yet', 'No Feedbacks Yet')}
            description={`${t(
              'All_clear_for_now_Keep_an_eye_out',
              'All clear for now. Keep an eye out',
            )}!`}
            imageVariant="file"
          />
        );
      case 'survey':
        // @ts-expect-error - TS2339 - Property 'content' does not exist on type 'HM_EXPERIENCE_SURVEY[]'.
        return surveyData?.content?.length ? (
          // @ts-expect-error - TS2339 - Property 'content' does not exist on type 'HM_EXPERIENCE_SURVEY[]'.
          surveyData.content
            .slice(0, 2)
            // @ts-expect-error - TS7006 - Parameter 'data' implicitly has an 'any' type. | TS7006 - Parameter 'index' implicitly has an 'any' type.
            .map((data, index) => (
              <SurveyCard key={index} surveyData={data} jobId={jobId} />
            ))
        ) : (
          <EmptyState
            title={t('No_Surveys_Yet', 'No Surveys Yet')}
            description={`${t(
              'All_clear_for_now_Keep_an_eye_out',
              'All clear for now. Keep an eye out',
            )}!`}
            imageVariant="file"
          />
        );
      case 'interviewFeedbacks':
        return recentInterviewData?.length ? (
          recentInterviewData
            .slice(0, 2)
            // @ts-expect-error - TS7006 - Parameter 'feedback' implicitly has an 'any' type. | TS7006 - Parameter 'index' implicitly has an 'any' type.
            .map((feedback, index) => (
              <InterviewFeedback
                key={index}
                feedbackData={feedback}
                from={from}
              />
            ))
        ) : (
          <EmptyState
            title={t('No_Feedbacks_Yet', 'No Feedbacks Yet')}
            description={`${t(
              'All_clear_for_now_Keep_an_eye_out',
              'All clear for now. Keep an eye out',
            )}!`}
            imageVariant="file"
          />
        );
      case 'completed':
        const allReviews: Array<{
          data: any;
          review: any;
        }> = [];
        // Combine both data sources and process them together
        const allCompletedData = [
          ...((completedData as {content?: Array<INTERVIEW_SCHEDULE>})
            ?.content || []),
          ...((
            completedCandidateFeedbacks as {content?: Array<CANDIDATE_FEEDBACK>}
          )?.content || []),
        ];
        allCompletedData.forEach((data: any) => {
          if (allReviews.length < 2 && data.interviewReviews?.length > 0) {
            // @ts-expect-error - TS7006 - Parameter 'review' implicitly has an 'any' type.
            data.interviewReviews.forEach((review) => {
              if (allReviews.length < 2) {
                allReviews.push({data, review});
              }
            });
          }
        });

        return allReviews.length ? (
          allReviews.map((item, index) => (
            <CompletedCard
              key={index}
              completedData={item.data}
              review={item.review}
              jobId={jobId}
              onUpdateFeedbacks={updateFeedbacks}
              from={from}
            />
          ))
        ) : (
          <EmptyState
            title={t('No_Feedbacks_Yet', 'No Feedbacks Yet')}
            description={`${t(
              'All_clear_for_now_Keep_an_eye_out',
              'All clear for now. Keep an eye out',
            )}!`}
            imageVariant="file"
          />
        );
      default:
        return null;
    }
  };

  const handleViewAll = () => {
    setIsPanelOpen(true);
  };

  React.useEffect(() => {
    dispatch(getCandidateFeedbacks('PENDING', jobId, 0));
    dispatch(getInterviewSchedules('PENDING', jobId, 0));
    dispatch(getInterviewSchedules('COMPLETED', jobId, 0));
    dispatch(getCandidateFeedbacks('COMPLETED', jobId, 0));
    dispatch(getHmExperienceSurveys('PENDING', jobId, 0));
  }, [jobId, isPanelOpen]);

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className={css.cardTitleContainer}>
            <SemanticIcon
              name="message"
              semantic="neutral"
              type="solid"
              size="small"
            />
            {t('Feedbacks', 'Feedbacks')}
            {pendingFeedbackCount > 0 && (
              <Badge text={String(pendingFeedbackCount)} fill="blue" />
            )}
          </CardTitle>
          <CardActions>
            {totalCount > 0 ? (
              <Button
                children={t('View_All', 'View All')}
                type="ghost"
                size="small"
                onClick={handleViewAll}
              />
            ) : null}
          </CardActions>
        </CardHeader>
        <div className={css.tabSlot}>
          <TabList
            onSelect={(tab) => {
              setSelected(tab);
            }}
            selectedTab={{
              label: selected.label,
              tabId: selected.tabId,
            }}
            size="medium"
            maxTabWidth={size200}
          >
            {tabsWithCount.map(({label, tabId}) => (
              <Tab key={tabId} label={label} tabId={tabId} />
            ))}
          </TabList>
        </div>
        {isLoading ||
        isCandidateFeedbacksLoading ||
        isInterviewSchedulesLoading ? (
          <div className={css.loaderContainer}>
            <CircularLoader size="large" />
          </div>
        ) : (
          <CardContent className={css.cardContent}>
            {renderContent()}
          </CardContent>
        )}
      </Card>
      {isPanelOpen && (
        <FeedbacksPanel
          isOpen={isPanelOpen}
          onClose={() => setIsPanelOpen(false)}
          // @ts-expect-error - TS2322 - Type 'HM_EXPERIENCE_SURVEY[]' is not assignable to type 'SurveyDataItem[]'.
          surveyData={surveyData}
          // @ts-expect-error - TS2322 - Type 'CANDIDATE_FEEDBACK[]' is not assignable to type 'ApplicationReviewDataItem[]'.
          applicantReviewData={applicantReviewData}
          isLoading={isLoading}
          recentInterviewData={recentInterviewData}
          // @ts-expect-error - TS2322 - Type 'INTERVIEW_SCHEDULE[]' is not assignable to type 'CompletedFeedbackData[]'.
          completedData={completedData}
          jobId={jobId}
          initialTab={selected}
          from={from}
        />
      )}
    </>
  );
};

export default Feedbacks;
